// The billing policy form's draft state, validation, and the dirty diff
// it sends.
//
// This is separate from the component because the diff carries the one
// rule that is expensive to get wrong, and it is worth testing on its
// own: **omit means "leave alone", `null` means "clear", a value means
// "set"** — and only eleven of the twenty-eight fields can be cleared.
//
// Sending `null` for one of the other seventeen is not an error and not
// a change: `BillingPolicyUpdate` types them as a plain `Option`, so
// serde reads `null` as absent and the field keeps its value. Verified
// against the running API — `{"grace_period_days": null}` left it at 10.
// That is worse than a rejection, because the form would report a save
// that did nothing, so blank is a validation error on those fields
// rather than something we try to send.

import {
  BillingPolicyReturn,
  BillingPolicyUpdate,
  DayCount,
  FeeBasis,
  InterestAccrual,
  InterestMethod,
  LateFeeType,
  LedgerEntryType,
  ProrationMethod,
  RoundingMode,
  TurnoverBalancePolicy,
} from '../clients/rust-api-client';
import { sameDecimal } from '../utils/billingHelpers';

/**
 * Every editable field, with the numeric ones held as strings.
 *
 * A string is what an `<input>` gives back, and — more to the point —
 * it is the only way to represent *blank*. A `number | null` draft
 * cannot tell "the user cleared this" from "the user is midway through
 * typing", and money must not become a float on the way through here
 * either.
 */
export interface PolicyDraft {
  // Late fees
  grace_period_days: string;
  late_fee_type: LateFeeType;
  late_fee_amount: string;
  late_fee_basis: FeeBasis;
  late_fee_max_per_period: string;
  late_fee_cap_total: string;
  late_fee_recurs: boolean;
  late_fee_compounds: boolean;
  nsf_fee: string;
  // Interest
  interest_enabled: boolean;
  interest_annual_rate: string;
  interest_method: InterestMethod;
  interest_accrual: InterestAccrual;
  interest_basis: FeeBasis;
  interest_day_count: DayCount;
  // Payments
  allow_credit_balances: boolean;
  payment_application_order: LedgerEntryType[];
  rounding_mode: RoundingMode;
  proration_method: ProrationMethod;
  // Collections
  hold_threshold_days: string;
  hold_threshold_amount: string;
  days_to_late_notice: string;
  days_to_demand: string;
  // Turnover
  turnover_balance_policy: TurnoverBalancePolicy;
  // Estoppel
  estoppel_fee: string;
  estoppel_fee_rush: string;
  estoppel_good_through_days: string;
  // Runs
  variance_threshold_percent: string;
}

export type PolicyErrors = Partial<Record<keyof PolicyDraft, string>>;

/** Money fields that clear to `null` when left blank. */
export const CLEARABLE_MONEY = [
  'late_fee_amount',
  'late_fee_max_per_period',
  'late_fee_cap_total',
  'nsf_fee',
  'hold_threshold_amount',
  'estoppel_fee',
  'estoppel_fee_rush',
] as const;

/** The one rate field, which clears the same way. */
export const CLEARABLE_RATE = ['interest_annual_rate'] as const;

/** Whole-number fields that clear to `null` when left blank. */
export const CLEARABLE_INT = [
  'hold_threshold_days',
  'days_to_late_notice',
  'days_to_demand',
] as const;

/**
 * Whole-number fields that **cannot** be cleared, so blank is invalid.
 * These are `NOT NULL` with a default on the way out, and a plain
 * `Option` on the way in — `null` would be read as "no change".
 */
export const REQUIRED_INT = [
  'grace_period_days',
  'estoppel_good_through_days',
  'variance_threshold_percent',
] as const;

const str = (value?: string | null): string => value ?? '';
const num = (value?: number | null): string =>
  value === null || value === undefined ? '' : String(value);

/** The server's policy, as the form holds it. */
export const fromPolicy = (p: BillingPolicyReturn): PolicyDraft => ({
  grace_period_days: num(p.grace_period_days),
  late_fee_type: p.late_fee_type,
  late_fee_amount: str(p.late_fee_amount),
  late_fee_basis: p.late_fee_basis,
  late_fee_max_per_period: str(p.late_fee_max_per_period),
  late_fee_cap_total: str(p.late_fee_cap_total),
  late_fee_recurs: p.late_fee_recurs,
  late_fee_compounds: p.late_fee_compounds,
  nsf_fee: str(p.nsf_fee),
  interest_enabled: p.interest_enabled,
  interest_annual_rate: str(p.interest_annual_rate),
  interest_method: p.interest_method,
  interest_accrual: p.interest_accrual,
  interest_basis: p.interest_basis,
  interest_day_count: p.interest_day_count,
  allow_credit_balances: p.allow_credit_balances,
  payment_application_order: [...(p.payment_application_order ?? [])],
  rounding_mode: p.rounding_mode,
  proration_method: p.proration_method,
  hold_threshold_days: num(p.hold_threshold_days),
  hold_threshold_amount: str(p.hold_threshold_amount),
  days_to_late_notice: num(p.days_to_late_notice),
  days_to_demand: num(p.days_to_demand),
  turnover_balance_policy: p.turnover_balance_policy,
  estoppel_fee: str(p.estoppel_fee),
  estoppel_fee_rush: str(p.estoppel_fee_rush),
  estoppel_good_through_days: num(p.estoppel_good_through_days),
  variance_threshold_percent: num(p.variance_threshold_percent),
});

const MONEY = /^\d+(\.\d{1,2})?$/;
// NUMERIC(6,4) — four places, and 12.0000 is twelve percent.
const RATE = /^\d+(\.\d{1,4})?$/;
const WHOLE = /^\d+$/;

/**
 * What is wrong with the draft, by field.
 *
 * Everything here is non-negative: there is no such thing as a negative
 * grace period or a late fee that pays the homeowner, and the backend
 * would reject them anyway. Catching it in the form means the message
 * names the field.
 */
export const validate = (draft: PolicyDraft): PolicyErrors => {
  const errors: PolicyErrors = {};

  CLEARABLE_MONEY.forEach((key) => {
    const value = draft[key].trim();
    if (value !== '' && !MONEY.test(value)) {
      errors[key] = 'Enter an amount like 25 or 25.00, or leave it blank.';
    }
  });

  CLEARABLE_RATE.forEach((key) => {
    const value = draft[key].trim();
    if (value !== '' && !RATE.test(value)) {
      errors[key] = 'Enter a rate like 12 or 12.5, or leave it blank.';
    }
  });

  CLEARABLE_INT.forEach((key) => {
    const value = draft[key].trim();
    if (value !== '' && !WHOLE.test(value)) {
      errors[key] = 'Enter a whole number of days, or leave it blank.';
    }
  });

  REQUIRED_INT.forEach((key) => {
    const value = draft[key].trim();
    if (value === '') {
      // Deliberately not "clear it": this field has no empty state, and
      // sending null would silently leave the old value in place.
      errors[key] = 'This needs a number — it cannot be left blank.';
    } else if (!WHOLE.test(value)) {
      errors[key] = 'Enter a whole number.';
    }
  });

  // A percentage late fee of 400% is far more likely to be someone
  // typing dollars into a percent field than a real policy.
  if (
    draft.late_fee_type === 'percent' &&
    MONEY.test(draft.late_fee_amount.trim()) &&
    Number(draft.late_fee_amount) > 100
  ) {
    errors.late_fee_amount =
      'Over 100%. This field is a percentage when the late fee type is ' +
      'Percentage — did you mean a flat amount?';
  }

  return errors;
};

export const hasErrors = (errors: PolicyErrors): boolean =>
  Object.keys(errors).length > 0;

const sameOrder = (a: LedgerEntryType[], b: LedgerEntryType[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Only what changed.
 *
 * A blank clearable field becomes an explicit `null`; an unchanged one
 * is left out entirely. Money and rates compare canonically, so
 * retyping `25.00` as `25` is not a change and does not produce a
 * write.
 */
export const toUpdate = (
  original: BillingPolicyReturn,
  draft: PolicyDraft
): BillingPolicyUpdate => {
  const update: BillingPolicyUpdate = {};

  const decimalField = (
    key: (typeof CLEARABLE_MONEY)[number] | (typeof CLEARABLE_RATE)[number]
  ): void => {
    const next = draft[key].trim();
    const before = original[key] ?? null;
    if (next === '') {
      if (before !== null) update[key] = null;
      return;
    }
    if (!sameDecimal(before, next)) update[key] = next;
  };

  CLEARABLE_MONEY.forEach(decimalField);
  CLEARABLE_RATE.forEach(decimalField);

  CLEARABLE_INT.forEach((key) => {
    const next = draft[key].trim();
    const before = original[key] ?? null;
    if (next === '') {
      if (before !== null) update[key] = null;
      return;
    }
    if (Number(next) !== before) update[key] = Number(next);
  });

  REQUIRED_INT.forEach((key) => {
    const next = draft[key].trim();
    // Blank never reaches here — validate() blocks the save — and if it
    // somehow did, omitting is the safe answer rather than sending a
    // null the backend would ignore.
    if (next === '') return;
    if (Number(next) !== original[key]) update[key] = Number(next);
  });

  const enums = [
    'late_fee_type',
    'late_fee_basis',
    'interest_method',
    'interest_accrual',
    'interest_basis',
    'interest_day_count',
    'rounding_mode',
    'proration_method',
    'turnover_balance_policy',
  ] as const;
  enums.forEach((key) => {
    if (draft[key] !== original[key]) {
      // Each key's draft type is its own enum; the union of the nine
      // defeats the indexed assignment, and they are checked one line
      // above by construction.
      (update as Record<string, unknown>)[key] = draft[key];
    }
  });

  const bools = [
    'late_fee_recurs',
    'late_fee_compounds',
    'interest_enabled',
    'allow_credit_balances',
  ] as const;
  bools.forEach((key) => {
    if (draft[key] !== original[key]) update[key] = draft[key];
  });

  if (
    !sameOrder(
      draft.payment_application_order,
      original.payment_application_order ?? []
    )
  ) {
    update.payment_application_order = draft.payment_application_order;
  }

  return update;
};

/** Whether anything would be written. */
export const isDirty = (
  original: BillingPolicyReturn,
  draft: PolicyDraft
): boolean => Object.keys(toUpdate(original, draft)).length > 0;

/**
 * Whether the form has been touched at all.
 *
 * Deliberately **not** {@link isDirty}, and the difference is a field
 * left blank that cannot be cleared: `toUpdate` correctly omits it, so
 * "would this write anything?" answers no, and gating the save button
 * on that leaves the button dead with no explanation of why. Enable on
 * edited, validate on submit, and let the field say what is wrong.
 *
 * Retyping `25.00` as `25` counts as edited even though it writes
 * nothing; submitting then reports that there was nothing to save,
 * which is true and more use than a button that will not press.
 */
export const isEdited = (
  original: BillingPolicyReturn,
  draft: PolicyDraft
): boolean => {
  const loaded = fromPolicy(original);
  return (Object.keys(loaded) as (keyof PolicyDraft)[]).some((key) => {
    const a = loaded[key];
    const b = draft[key];
    return Array.isArray(a) && Array.isArray(b)
      ? !sameOrder(a, b as LedgerEntryType[])
      : a !== b;
  });
};
